import values from 'lodash/values';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';

import CheckBadgeIcon from '../../../../static/icons/ic_check_circle_dynamic_light_16.svg';
import EmptyCircleIcon from '../../../../static/icons/ic_empty_circle.svg';
import ShuffleIcon from '../../../../static/icons/ic_shuffle_24.svg';
import * as Constants from '../../../constants';
import QATags from '../../../constants/QATags';
import { tooltipMessageTypes } from '../../../constants/Tooltip';
import { handlerNoPropagate } from '../../../lib/EventUtils';
import { createBEM } from '../../../lib/utils/css';
import Icon from '../../Icon/Icon';
import Tooltip from '../../lib/Tooltip/Tooltip';

import './Badge.scss';

const BADGE_TYPES = Constants.Badge.BadgeTypes;
const BADGE_TEXT = Constants.Badge.BadgeText;

// This component is used to show a toggle-able icon, that will have a disabled
// and enabled state and will call enabled/disabled callbacks when clicked. Example
// use case would be showing an icon on the edge of a grid item.
class Badge extends React.Component {
  static propTypes = {
    intl: PropTypes.object.isRequired,
    badgeType: PropTypes.oneOf(values(BADGE_TYPES)),
    badgeText: PropTypes.string,
    isIcon: PropTypes.bool,
    isShuffleDisabled: PropTypes.bool,
    onClick: PropTypes.func,
    onClickIfShuffleDisabled: PropTypes.func,
    qaTag: PropTypes.oneOf(values(QATags)),
    isDark: PropTypes.bool,
    isLight: PropTypes.bool,
    isInline: PropTypes.bool,
    isOpaque: PropTypes.bool,
    isFilled: PropTypes.bool,
    isBlue: PropTypes.bool,
  };
  getBadgeIconProps = () => {
    let glyph;
    switch (this.props.badgeType) {
      case BADGE_TYPES.COLLECTED:
        glyph = CheckBadgeIcon;
        break;
      case BADGE_TYPES.SHUFFLE:
        glyph = this.props.isShuffleDisabled ? EmptyCircleIcon : ShuffleIcon;
        break;
      default:
        return null;
    }
    return {
      className: 'Badge__icon',
      glyph,
    };
  };

  getBadgeText = () => {
    switch (this.props.badgeType) {
      case BADGE_TYPES.EXPLICIT_SHORT:
        return this.props.intl.formatMessage(BADGE_TEXT.EXPLICIT_SHORT);
      case BADGE_TYPES.CLEAN:
        return this.props.intl.formatMessage(BADGE_TEXT.CLEAN);
      case BADGE_TYPES.CLEAN_SHORT:
        return this.props.intl.formatMessage(BADGE_TEXT.CLEAN_SHORT);
      case BADGE_TYPES.RADIO:
        return this.props.intl.formatMessage(BADGE_TEXT.RADIO);
      case BADGE_TYPES.UNAVAILABLE:
        return this.props.intl.formatMessage(BADGE_TEXT.UNAVAILABLE);
      case BADGE_TYPES.COLLECTED:
        return this.props.intl.formatMessage(BADGE_TEXT.COLLECTED);
      case BADGE_TYPES.SHUFFLE: {
        const shuffleText = this.props.isShuffleDisabled
          ? BADGE_TEXT.SHUFFLE_OFF
          : BADGE_TEXT.SHUFFLE_ON;
        return this.props.intl.formatMessage(shuffleText);
      }
      case BADGE_TYPES.TAKEOVER_MODES:
        return this.props.intl.formatMessage(BADGE_TEXT.ARTIST_MODES);
      case BADGE_TYPES.CURATED_MODES:
        return this.props.intl.formatMessage(BADGE_TEXT.CURATED_MODES);
      default:
        return this.props.badgeText || null;
    }
  };
  renderToolTip = () => {
    if (
      this.props.badgeType === BADGE_TYPES.EXPLICIT_SHORT ||
      this.props.badgeType === BADGE_TYPES.CLEAN_SHORT
    ) {
      const tooltipText =
        this.props.badgeType === BADGE_TYPES.EXPLICIT_SHORT
          ? tooltipMessageTypes.EXPLICIT
          : tooltipMessageTypes.CLEAN;
      return <Tooltip tipText={tooltipText} style={{ height: '1.4rem', width: '1.4rem' }} />;
    }
  };
  renderBadgeContent = () => {
    if (this.props.isIcon) {
      const badgeIconProps = this.getBadgeIconProps();
      return <Icon {...badgeIconProps} />;
    }
    const badgeText = this.getBadgeText();
    if (badgeText) {
      return <div className='Badge__text'>{badgeText}</div>;
    }
  };
  render() {
    const badgeContent = this.renderBadgeContent();
    if (!badgeContent) {
      return null;
    }
    const callback = this.props.isShuffleDisabled
      ? this.props.onClickIfShuffleDisabled
      : this.props.onClick;
    const classes = createBEM('Badge', {
      button: callback,
      light: this.props.isLight,
      dark: this.props.isDark,
      red: this.props.badgeType === BADGE_TYPES.EXPLICIT_SHORT,
      filled: this.props.isFilled,
      opaque: this.props.isOpaque,
      inline: this.props.isInline,
      icon: this.props.isIcon,
      collected: this.props.isIcon && this.props.badgeType === BADGE_TYPES.COLLECTED,
      shuffle: this.props.isIcon && this.props.badgeType === BADGE_TYPES.SHUFFLE,
      blue: this.props.isBlue,
      disabled: this.props.isShuffleDisabled,
    });
    if (callback) {
      const callbackNoPropagate = callback ? handlerNoPropagate(callback) : null;
      return (
        <button
          className={classes}
          data-qa={this.props.qaTag}
          onClick={callbackNoPropagate}
          aria-label={this.getBadgeText()}
          type='button'
        >
          {badgeContent}
        </button>
      );
    }
    return (
      <div className={classes} data-qa={this.props.qaTag}>
        {badgeContent}
        {this.renderToolTip()}
      </div>
    );
  }
}

export default injectIntl(Badge);
